// Compound-SELECT set-operator switch planner. Replaces the recorded separator
// span (keyword THROUGH any ALL/DISTINCT modifier) of `compoundOperators[branchIndex]`
// with the new operator text, byte-precisely — every other byte of the statement
// is preserved. `branchIndex` is the SEPARATOR index: the operator that precedes
// `compoundBranches[branchIndex]` (1:1 with the UI's `separators[branchIndex]`).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — set-operator switch (§2.3 S7).

import type { PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { replaceEdit } from './applyStructuralEdit.shared';

type SetOperatorEdit = Extract<StructuralEdit, { op: 'modify-set-operator' }>;

export function planSetOperatorEdit(
  ctx: EditCtx,
  edit: SetOperatorEdit,
): readonly PlannedByteEdit[] {
  const operators = ctx.structure.compoundOperators;
  // No compound chain (plain SELECT), or the separator index is out of range —
  // nothing to target. Defense in depth: the UI only offers valid indices.
  if (!operators || edit.branchIndex < 0 || edit.branchIndex >= operators.length) {
    return [];
  }
  const op = operators[edit.branchIndex];
  // `ALL` only rides UNION in the UI, but the planner is spelling-agnostic — it
  // emits whatever the op requests and swallows the previous modifier via the
  // modifier-inclusive range.
  const replacement = edit.all ? `${edit.newOperator} ALL` : edit.newOperator;
  return [
    replaceEdit(
      ctx,
      op.range.startOffset,
      op.range.endOffset,
      replacement,
      'modify-set-operator',
    ),
  ];
}
